import React from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Play, NotebookIcon, Calendar, Target, RefreshCcw } from "lucide-react";
import CustomButton from "../customButtom/CustomButton";
import Sidebar from "../Sidebar";
import { assessmentData } from "../../constants/constants";
import { useSelector } from "react-redux";
import { COLORS, HEADING_COLOR } from "../../constants/colors";

const AILiteracyAssessmentDashboard = ({ onStartQuiz }) => {
  const navigate = useNavigate();
  const { quizCompleted, totalScore, proficiencyLevel } = useSelector((state) => state.user);

  const displaySections = assessmentData.slice(0, 6);

  const renderSectionCard = (section, index) => {
    return (
      <div key={index} className="bg-white border border-gray-400 rounded-lg p-5 hover:shadow-md transition-shadow">
        <div className="mb-3">
          <div className="flex justify-between gap-3 mb-1">
            <h3 className="font-semibold text-gray-900 text-medium">{section.section}</h3>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm mb-3">
          {section.questions[0]?.hint || "Assessment questions to evaluate your knowledge in this area."}
        </p>
      </div>
    );
  };

  const renderResultsView = () => {
    if (!quizCompleted) return null;

    const score = totalScore || 0;
    const maxScore = 60; 
    const level = proficiencyLevel || "Advanced";
    const completionDate = new Date().toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric' 
    });

    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-300 rounded-2xl shadow-xl p-9">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="w-40 h-40 border-2 rounded-full bg-purple-50 flex items-center justify-center" style={{ borderColor: COLORS.purple }}>
                <div className="text-center">
                  <div className="text-6xl font-bold" style={{ color: COLORS.purple }}>{score}</div>
                  <div className="text-xl text-gray-500">/{maxScore}</div>
                </div>
              </div>
              
              <div>
                <div className="inline-flex items-center px-7 py-3 rounded-full bg-purple-100 text-purple-700 font-semibold text-lg mb-2">
                  {level} Level
                </div>
                <div className="ml-2 flex items-center text-gray-500 text-sm">
                  <Calendar className="w-4 h-4 mr-1" />
                  Completed on {completionDate}
                </div>
              </div>
            </div>
            
            {/* Retake Button */}
            <button 
              onClick={onStartQuiz}
              className="flex items-center gap-2 px-5 py-2 rounded-lg font-medium bg-blue-50 text-blue-700 hover:bg-blue-200 border border-blue-200"
            >
              <RefreshCcw className="w-4 h-4" />
              Retake Assessment
            </button>
          </div>
        </div>

        {/* Call to Action Card */}
        <div className="bg-purple-50 border border-gray-300 rounded-2xl shadow-lg p-8">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Ready for the Next Step?</h2>
            <p className="text-gray-600 mb-6">Generate your personalized upskilling plan based on your AI literacy results</p>
            <button 
              onClick={() => navigate("/dashboard/skills-and-learning/upskilling-pathway-config")}
              className="text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto"
              style={{ backgroundColor: COLORS.purple }}
            >
              <Target className="w-5 h-5" />
              Generate Upskilling Plan
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render assessment view when user hasn't taken the assessment
  const renderAssessmentView = () => {
    return (
      <div className="border border-gray-400 rounded-2xl shadow-lg p-8">
        {/* Assessment Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.purple }}>
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">AI Literacy Assessment</h2>
              <p className="text-gray-500">Takes between 5 to 10 Mins</p>
            </div>
          </div>
        </div>

        {/* Assessment Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {displaySections.map(renderSectionCard)}
        </div>

        {/* Call to Action Button */}
        <div className="text-center">
          <CustomButton
            label="Start Assessment Now"
            onClick={onStartQuiz}
            buttonClass="text-black border border-gray-400 px-12 py-3 text-lg font-semibold rounded-lg flex items-center gap-3 mx-auto hover:bg-gray-200"
            leftIcon={<Play className="w-5 h-5" />}
          />
        </div>

        {/* Assessment Summary */}
        <div className="text-center mt-6 text-gray-500 text-sm">
          <div className="flex items-center justify-center gap-6">
            <span className="flex items-center gap-1">
              <NotebookIcon className="w-4 h-4" /> 12 total questions
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Sidebar>
      <div className="max-w-6xl mx-auto px-4">
        {/* Main Content - Show results if completed, otherwise show assessment */}
        {quizCompleted ? (
          <>
            {/* Results Header Section */}
            <div className="text-center py-8">
              <h1 className="text-3xl font-bold mb-3" style={{ color: HEADING_COLOR }}>
                Your AI Literacy Assessment Score
              </h1>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                Congratulations! You've completed the AI Literacy Assessment. Here's how you performed and what's next for your learning journey.
              </p>
            </div>
            {renderResultsView()}
          </>
        ) : (
          <>
            {/* Header Section - Only show when not completed */}
            <div className="text-center py-8">
              <h1 className="text-3xl font-bold mb-3" style={{ color: HEADING_COLOR }}>
                Discover Your AI Readiness for your Career
              </h1>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                Take our comprehensive assessment to unlock personalized learning pathways and accelerate your career in the AI era.
              </p>
            </div>
            {renderAssessmentView()}
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default AILiteracyAssessmentDashboard;
